// @flow

import RestClient from 'frontend_core/dist/api/rest/client'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import { createAction, handleActions } from 'redux-actions'
import { formatErrorMessages } from 'frontend_core/dist/api/rest'
import { messageManager } from 'helpers/MessageManager'
import { actions as networkActions } from 'redux/modules/network'

// FlowType
type Action = {
  type: string,
  payload: {},
}

type EventSetting = {
  can_receive_feature_mail: boolean,
  can_receive_feature_push: boolean,
  can_receive_reminder_mail: boolean,
  can_receive_reminder_push: boolean,
  can_receive_recommend_mail: boolean,
  can_receive_recommend_push: boolean,
  can_receive_action_mail: boolean,
  can_receive_action_push: boolean,

  canReceiveFeatureMail: boolean,
  canReceiveFeaturePush: boolean,
  canReceiveReminderMail: boolean,
  canReceiveReminderPush: boolean,
  canReceiveRecommendMail: boolean,
  canReceiveRecommendPush: boolean,
  canReceiveActionMail: boolean,
  canReceiveActionPush: boolean,
}

/**
 ************************************************************
 * Actions
 ************************************************************
 **/

const BASE_ACTION = '@@event'
export const actions = {
  setEventSetting: createAction(`${BASE_ACTION}/SET_EVENT_SETTING`),
  fetchEventSetting: createAction(`${BASE_ACTION}/FETCH_EVENT_SETTING`),
  updateEventSetting: createAction(`${BASE_ACTION}/UPDATE_EVENT_SETTING`),
}

/**
 ************************************************************
 * State
 ************************************************************
 **/

const initialState = {
  canReceiveFeatureMail: false,
  canReceiveFeaturePush: false,
  canReceiveReminderMail: false,
  canReceiveReminderPush: false,
  canReceiveRecommendMail: false,
  canReceiveRecommendPush: false,
  canReceiveActionMail: false,
  canReceiveActionPush: false,
  errorMessages: [],
}

/**
 ************************************************************
 * Reducer
 ************************************************************
 **/

export function validate(eventSetting: EventSetting, errors?: {} = {}) {
  return {
    canReceiveFeatureMail: eventSetting.can_receive_feature_mail,
    canReceiveFeaturePush: eventSetting.can_receive_feature_push,
    canReceiveReminderMail: eventSetting.can_receive_reminder_mail,
    canReceiveReminderPush: eventSetting.can_receive_reminder_push,
    canReceiveRecommendMail: eventSetting.can_receive_recommend_mail,
    canReceiveRecommendPush: eventSetting.can_receive_recommend_push,
    canReceiveActionMail: eventSetting.can_receive_action_mail,
    canReceiveActionPush: eventSetting.can_receive_action_push,
    errorMessages: formatErrorMessages(errors),
  }
}

export const reducer = handleActions(
  {
    [actions.setEventSetting().type]: (state, action) =>
      Object.assign({}, state, action.payload),
    [actions.fetchEventSetting().type]: (state, action) => state,
    [actions.updateEventSetting().type]: (state, action) => state,
  },
  initialState,
)

/**
 ************************************************************
 * Async Action
 ************************************************************
 **/

function* fetchEventSetting(action: {
  payload: { restClient: any, id: string },
}) {
  const { restClient, id } = action.payload
  try {
    const res = yield call(
      [restClient, 'get'],
      `1/users/${id || 'me'}/event_setting`,
    )
    yield put(actions.setEventSetting(validate(res.data)))
  } catch (error) {
    console.error(error)
  }
}

function* updateEventSetting(action: {
  payload: {
    restClient: any,
    eventSetting: EventSetting,
    message: string,
    doneUrl: string,
  },
}) {
  const { restClient, eventSetting, message, doneUrl } = action.payload
  const data = new FormData()
  data.append('can_receive_feature_mail', eventSetting.canReceiveFeatureMail)
  data.append('can_receive_feature_push', eventSetting.canReceiveFeaturePush)
  data.append('can_receive_reminder_mail', eventSetting.canReceiveReminderMail)
  data.append('can_receive_reminder_push', eventSetting.canReceiveReminderPush)
  data.append(
    'can_receive_recommend_mail',
    eventSetting.canReceiveRecommendMail,
  )
  data.append(
    'can_receive_recommend_push',
    eventSetting.canReceiveRecommendPush,
  )
  data.append('can_receive_action_mail', eventSetting.canReceiveActionMail)
  data.append('can_receive_action_push', eventSetting.canReceiveActionPush)

  yield put(networkActions.setLoading(true))

  try {
    const res = yield call(
      [restClient, 'patch'],
      '1/users/me/event_setting',
      data,
    )
    if (!!message) messageManager.success(message)
    if (!!doneUrl) {
      yield put(networkActions.setRedirectUrl(doneUrl))
    }
    if (res.data) {
      yield put(actions.setEventSetting(validate(res.data)))
    }
  } catch (error) {
    console.error(error)
    if (error.response && error.response.status === 400)
      messageManager.warning(error.message)
    else messageManager.error(error.message)
  }

  yield put(networkActions.setLoading(false))
}

export const sagas = [
  takeEvery(actions.fetchEventSetting().type, fetchEventSetting),
  takeEvery(actions.updateEventSetting().type, updateEventSetting),
]
